import React, { createContext, type ReactNode, useContext } from "react";
import ErrorFallback from "~components/ErrorHandler/ErrorFallback";

type ErrorContextType = {
  hasError: boolean;
  error: Error | null;
  resetError: () => void;
};

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

class ErrorBoundary extends React.Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state when an error is thrown
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to an external service if needed
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContext.Provider
          value={{
            hasError: this.state.hasError,
            error: this.state.error,
            resetError: this.resetError,
          }}
        >
          <ErrorFallback />
        </ErrorContext.Provider>
      );
    }

    return this.props.children;
  }
}

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within an ErrorBoundary");
  }
  return context;
};

export const ErrorProvider = ({ children }: { children: ReactNode }) => <ErrorBoundary>{children}</ErrorBoundary>;
